import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const chapters = [
  { n: "01", title: "Collect", body: "Sealed, GPS-tracked logistics collect your assets on-site. Every serial number is scanned into a documented chain of custody the moment it leaves your floor." },
  { n: "02", title: "Sanitize", body: "Drives are wiped to NIST 800-88 standards or physically destroyed. You receive a per-device certificate of data destruction — no exceptions, no gaps." },
  { n: "03", title: "Recover", body: "Functional hardware is graded, refurbished and remarketed into a global channel. We return maximum value to you through transparent settlement." },
  { n: "04", title: "Recycle", body: "Everything else enters a zero-landfill R2v3 downstream. Metals, boards and plastics are recovered — you get the environmental impact report to prove it." },
];

const MB_IMG =
  "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxtb3RoZXJib2FyZCUyMGNpcmN1aXQlMjBib2FyZCUyMGUtd2FzdGV8ZW58MHx8fHwxNzg0NTM3MTg4fDA&ixlib=rb-4.1.0&q=85";

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section id="process" ref={ref} className="bg-[#0a0a0a] text-[#f7f7f6] py-20 md:py-32 relative overflow-hidden grain">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 relative z-10">
        <div className="mb-16 md:mb-24">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-4">/ The process</p>
          <h2 className="font-display font-extrabold uppercase tracking-tighter text-4xl md:text-6xl lg:text-7xl leading-[0.88] max-w-4xl">
            A closed loop, from your rack to a second life.
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-0">
            {chapters.map((c, i) => (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`process-chapter-${c.n}`}
                className="grid grid-cols-12 gap-4 py-8 md:py-10 border-t border-white/15 items-start group"
              >
                <span className="col-span-12 md:col-span-4 font-display font-black text-6xl md:text-8xl tracking-tighter text-transparent [-webkit-text-stroke:1.5px_#c87e4a] group-hover:[-webkit-text-stroke-color:#f7f7f6] transition-all leading-none">
                  {c.n}
                </span>
                <div className="col-span-12 md:col-span-8">
                  <h3 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-3">{c.title}</h3>
                  <p className="font-sans text-white/60 leading-relaxed max-w-lg">{c.body}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="lg:col-span-5 relative hidden lg:block">
            <motion.div style={{ y }} className="sticky top-28 h-[70vh] overflow-hidden border border-white/10">
              <img src={MB_IMG} alt="Circuit board recycling" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <p className="font-mono text-xs uppercase tracking-widest text-primary">Zero-landfill downstream</p>
                <p className="font-display font-extrabold text-2xl tracking-tight mt-1">Every gram accounted for.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
